import { z } from 'zod';
import { ICON_NAMES } from './icons';
export const AI_EDITOR_CONTRACT_VERSION = 1;
export const AI_EDITOR_MAX_OPERATIONS = 50;
export const AI_EDITOR_MAX_CONTEXT_ELEMENTS = 200;
/**
 * Context is sent in two tiers so a request costs what the task needs, not what
 * the page weighs:
 *
 * - detail  — the selected subtree plus its ancestors. Carries effective styles.
 * - outline — every other element, as a 5-field map with no styles, so the model
 *             can still name and target sections it was not given detail for.
 *
 * The editor fills the detail tier first and spends whatever is left on outline,
 * staying under AI_EDITOR_MAX_REQUEST_CHARS.
 */
export const AI_EDITOR_MAX_DETAIL_ELEMENTS = 80;
export const AI_EDITOR_MAX_REQUEST_CHARS = 15_000;
/** Keeps one user instruction concise; page context has its own separate budget. */
export const AI_EDITOR_MAX_MESSAGE_CHARS = 500;
export const AI_EDITOR_MAX_HISTORY_MESSAGES = 4;
export const AI_EDITOR_MAX_HISTORY_CHARS = 400;
/** Enough to identify a section in the outline tier without paying for its copy. */
export const AI_EDITOR_OUTLINE_CONTENT_CHARS = 80;
/** Shared client/server gate for allowing AI to change an existing href. */
export function messageExplicitlyRequestsAiLinkChange(message) {
    const normalized = message.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const destination = /\b(enlace|link|href|url|destino|pagina|sitio|web|mailto|telefono)\b/.test(normalized)
        || /\b(lleve|dirija|redirija|apunte|abra|navegue)\b/.test(normalized);
    const action = /\b(pon|poner|coloca|colocar|agrega|agregar|cambia|cambiar|asigna|asignar|enlaza|vincula|lleve|dirija|redirija|apunte|abra)\w*/.test(normalized);
    const containsTarget = /(?:https?:\/\/|mailto:|tel:|(?:^|\s)\/[a-z0-9#?/_-]+|#[a-z][a-z0-9_-]*)/i.test(message);
    return (destination && action) || (containsTarget && action);
}
export const AI_EDITOR_OPERATION_TYPES = [
    'set_content',
    'set_attribute',
    'patch_styles',
    'set_color_token',
    'set_icon',
    'patch_theme_colors',
    'patch_theme_fonts',
    'patch_button_style',
    'patch_card_style',
    'patch_icon_style',
    'patch_brand_logo',
    'patch_text_style',
    'set_visibility',
    'move_element',
    'duplicate_element',
    'delete_element',
    'insert_component',
    'apply_design_style',
];
export const aiBreakpointSchema = z.enum(['desktop', 'tablet', 'mobile']);
export const aiInteractionStateSchema = z.enum(['normal', 'hover', 'pressed', 'focus', 'current', 'open']);
export const aiEditorOperationTypeSchema = z.enum(AI_EDITOR_OPERATION_TYPES);
export const AI_EDITABLE_STYLE_PROPERTIES = [
    'background', 'backgroundColor', 'backgroundSize', 'backgroundPosition', 'backgroundRepeat',
    'color', 'fontSize', 'fontWeight', 'fontFamily', 'fontStyle', 'textAlign', 'textDecoration',
    'textTransform', 'lineHeight', 'letterSpacing',
    'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'width', 'height', 'minWidth', 'minHeight', 'maxWidth', 'maxHeight', 'aspectRatio',
    'border', 'borderTop', 'borderRight', 'borderBottom', 'borderLeft', 'borderWidth',
    'borderStyle', 'borderColor', 'borderRadius', 'outline', 'outlineOffset',
    'display', 'flexDirection', 'flexWrap', 'flex', 'flexGrow', 'flexShrink', 'flexBasis',
    'justifyContent', 'alignItems', 'alignContent', 'alignSelf', 'justifySelf',
    'gap', 'rowGap', 'columnGap',
    'gridTemplateColumns', 'gridTemplateRows', 'gridAutoFlow', 'gridColumn', 'gridRow',
    'justifyItems', 'placeItems', 'direction',
    'position', 'inset', 'top', 'right', 'bottom', 'left', 'zIndex',
    'opacity', 'visibility', 'overflow', 'overflowX', 'overflowY',
    'objectFit', 'objectPosition', 'boxShadow', 'transform', 'transformOrigin',
];
export const aiEditableStylePropertySchema = z.enum(AI_EDITABLE_STYLE_PROPERTIES);
const unsafeCssValue = /[{};<>\r\n]|(?:url|expression)\s*\(|javascript:|@import/i;
export const aiSafeCssValueSchema = z.string()
    .trim()
    .min(1)
    .max(240)
    .refine((value) => !unsafeCssValue.test(value), 'Unsafe CSS value');
export const aiStylePatchSchema = z.record(aiEditableStylePropertySchema, aiSafeCssValueSchema)
    .refine((styles) => Object.keys(styles).length > 0, 'At least one style is required')
    .refine((styles) => Object.keys(styles).length <= 24, 'Too many styles in one operation');
export const AI_THEME_COLOR_TOKENS = [
    'primary', 'backgroundSecondary', 'surface', 'background', 'muted',
    'text', 'accent', 'card', 'border',
];
export const aiThemeColorTokenSchema = z.enum(AI_THEME_COLOR_TOKENS);
export const aiThemeColorPatchSchema = z.record(aiThemeColorTokenSchema, aiSafeCssValueSchema)
    .refine((colors) => Object.keys(colors).length > 0, 'At least one color is required')
    .refine((colors) => Object.keys(colors).length <= AI_THEME_COLOR_TOKENS.length, 'Too many theme colors');
/** Fonts that are loaded by both the editor canvas and published sites. */
export const AI_FONT_FAMILIES = [
    'Inter', 'Roboto', 'Open Sans', 'Poppins', 'Montserrat', 'Lato', 'Raleway',
    'Nunito', 'Work Sans', 'Rubik', 'DM Sans', 'Mulish', 'Quicksand', 'PT Sans',
    'Oswald', 'Bebas Neue', 'Playfair Display', 'Merriweather', 'Lora',
];
export const aiFontFamilySchema = z.enum(AI_FONT_FAMILIES);
const operationBase = {
    id: z.string().trim().min(1).max(64).regex(/^[a-zA-Z0-9_-]+$/),
    reason: z.string().trim().min(1).max(240),
};
export const aiSetContentOperationSchema = z.object({
    ...operationBase,
    type: z.literal('set_content'),
    elementId: z.string().trim().min(1).max(160),
    field: z.enum(['content', 'accentContent', 'afterContent', 'alt', 'ariaLabel', 'label']),
    value: z.string().max(5_000),
}).strict();
export const aiSetAttributeOperationSchema = z.object({
    ...operationBase,
    type: z.literal('set_attribute'),
    elementId: z.string().trim().min(1).max(160),
    field: z.enum(['href', 'src', 'anchorId', 'iconName']),
    value: z.string().trim().min(1).max(2_000)
        .refine((value) => !/[<>{}\r\n]|javascript:|data:text\//i.test(value), 'Unsafe attribute value'),
}).strict();
export const aiPatchStylesOperationSchema = z.object({
    ...operationBase,
    type: z.literal('patch_styles'),
    elementId: z.string().trim().min(1).max(160),
    breakpoint: aiBreakpointSchema,
    interactionState: aiInteractionStateSchema.default('normal'),
    responsive: z.boolean().optional(),
    styles: aiStylePatchSchema,
}).strict();
export const aiSetColorTokenOperationSchema = z.object({
    ...operationBase,
    type: z.literal('set_color_token'),
    elementId: z.string().trim().min(1).max(160),
    property: z.enum(['color', 'background', 'backgroundColor', 'borderColor', 'boxShadowColor']),
    tokenId: aiThemeColorTokenSchema,
}).strict();
export const aiIconNameSchema = z.string().refine((value) => ICON_NAMES.includes(value), 'Unknown icon');
export const aiSetIconOperationSchema = z.object({
    ...operationBase,
    type: z.literal('set_icon'),
    elementId: z.string().trim().min(1).max(160),
    iconName: aiIconNameSchema,
    iconSize: z.number().int().min(8).max(256).optional(),
}).strict();
export const aiPatchThemeColorsOperationSchema = z.object({
    ...operationBase,
    type: z.literal('patch_theme_colors'),
    colors: aiThemeColorPatchSchema,
}).strict();
export const aiPatchThemeFontsOperationSchema = z.object({
    ...operationBase,
    type: z.literal('patch_theme_fonts'),
    headingFont: aiFontFamilySchema,
    bodyFont: aiFontFamilySchema,
}).strict();
export const AI_BUTTON_STYLE_PROPERTIES = [
    'background', 'backgroundColor', 'color',
    'fontSize', 'fontWeight', 'fontFamily', 'fontStyle', 'textAlign',
    'textDecoration', 'textTransform', 'lineHeight', 'letterSpacing',
    'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'minWidth', 'minHeight',
    'border', 'borderTop', 'borderRight', 'borderBottom', 'borderLeft',
    'borderWidth', 'borderStyle', 'borderColor', 'borderRadius',
    'boxShadow',
];
export const aiButtonStylePropertySchema = z.enum(AI_BUTTON_STYLE_PROPERTIES);
const aiButtonStyleValuesSchema = z.record(aiButtonStylePropertySchema, aiSafeCssValueSchema);
export const aiButtonStylePatchSchema = z.record(aiButtonStylePropertySchema, aiSafeCssValueSchema)
    .refine((styles) => Object.keys(styles).length > 0, 'At least one button style is required')
    .refine((styles) => Object.keys(styles).length <= AI_BUTTON_STYLE_PROPERTIES.length, 'Too many button styles');
export const aiPatchButtonStyleOperationSchema = z.object({
    ...operationBase,
    type: z.literal('patch_button_style'),
    // button-link is deliberately excluded: links are not AI-editable.
    styleId: z.enum(['button-primary', 'button-secondary']),
    breakpoint: aiBreakpointSchema,
    styles: aiButtonStylePatchSchema,
}).strict();
export const AI_CARD_STYLE_PROPERTIES = [
    'background', 'backgroundColor', 'backgroundImage',
    'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'border', 'borderTop', 'borderRight', 'borderBottom', 'borderLeft',
    'borderWidth', 'borderStyle', 'borderColor', 'borderRadius',
    'boxShadow',
];
export const aiCardStylePropertySchema = z.enum(AI_CARD_STYLE_PROPERTIES);
const aiCardStyleValuesSchema = z.record(aiCardStylePropertySchema, aiSafeCssValueSchema);
export const aiCardStylePatchSchema = z.record(aiCardStylePropertySchema, aiSafeCssValueSchema)
    .refine((styles) => Object.keys(styles).length > 0, 'At least one card style is required')
    .refine((styles) => Object.keys(styles).length <= AI_CARD_STYLE_PROPERTIES.length, 'Too many card styles');
export const aiPatchCardStyleOperationSchema = z.object({
    ...operationBase,
    type: z.literal('patch_card_style'),
    styleId: z.string().trim().min(1).max(120),
    breakpoint: aiBreakpointSchema,
    styles: aiCardStylePatchSchema,
}).strict();
export const AI_ICON_STYLE_PROPERTIES = [
    'color', 'background', 'backgroundColor',
    'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'border', 'borderWidth', 'borderStyle', 'borderColor', 'borderRadius',
    'boxShadow',
];
export const aiIconStylePropertySchema = z.enum(AI_ICON_STYLE_PROPERTIES);
const aiIconStyleValuesSchema = z.record(aiIconStylePropertySchema, aiSafeCssValueSchema);
export const aiIconStylePatchSchema = z.record(aiIconStylePropertySchema, aiSafeCssValueSchema)
    .refine((styles) => Object.keys(styles).length > 0, 'At least one icon style is required');
export const aiPatchIconStyleOperationSchema = z.object({
    ...operationBase,
    type: z.literal('patch_icon_style'),
    styleId: z.string().trim().min(1).max(120),
    breakpoint: aiBreakpointSchema,
    styles: aiIconStylePatchSchema.optional(),
    iconSize: z.number().int().min(1).max(256).optional(),
}).strict();
const aiBrandLogoImageSchema = z.string().max(2_000).refine((value) => value === '' || /^(?:https?:\/\/|\/(?!\/))/i.test(value), 'Unsafe logo image URL');
export const aiBrandLogoPatchSchema = z.object({
    name: z.string().max(60).optional(),
    nameFont: aiFontFamilySchema.optional(),
    nameColor: aiSafeCssValueSchema.optional(),
    nameColorDark: aiSafeCssValueSchema.optional(),
    nameSize: z.number().int().min(8).max(96).optional(),
    nameWeight: z.number().int().min(100).max(900).optional(),
    mark: z.enum(['none', 'icon', 'image']).optional(),
    iconName: aiIconNameSchema.optional(),
    iconColor: aiSafeCssValueSchema.optional(),
    iconColorDark: aiSafeCssValueSchema.optional(),
    iconSize: z.number().int().min(6).max(96).optional(),
    iconBackground: aiSafeCssValueSchema.optional(),
    iconBackgroundDark: aiSafeCssValueSchema.optional(),
    iconBackgroundOpacity: z.number().int().min(0).max(100).optional(),
    iconRadius: z.number().int().min(0).max(120).optional(),
    imageLight: aiBrandLogoImageSchema.optional(),
    imageDark: aiBrandLogoImageSchema.optional(),
    imageSize: z.number().int().min(6).max(320).optional(),
    showName: z.boolean().optional(),
}).strict().refine((patch) => Object.keys(patch).length > 0, 'At least one logo field is required');
export const aiPatchBrandLogoOperationSchema = z.object({
    ...operationBase,
    type: z.literal('patch_brand_logo'),
    patch: aiBrandLogoPatchSchema,
}).strict();
/**
 * Typography roles carry their own styles in the theme, so "pon el texto en
 * verde" belongs here and not in `patch_theme_colors`: recolouring the global
 * `text` token repaints everything that references it, not just the copy.
 */
/** Mirrors the editor's typography components: h1–h6, paragraph and eyebrow. */
export const AI_GLOBAL_TEXT_STYLE_IDS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'small'];
export const AI_TEXT_STYLE_PROPERTIES = [
    'color', 'fontSize', 'fontWeight', 'fontFamily', 'fontStyle',
    'lineHeight', 'letterSpacing', 'textTransform', 'textAlign', 'textDecoration',
];
export const aiTextStylePropertySchema = z.enum(AI_TEXT_STYLE_PROPERTIES);
export const aiTextStylePatchSchema = z.record(aiTextStylePropertySchema, aiSafeCssValueSchema)
    .refine((styles) => Object.keys(styles).length > 0, 'At least one text style is required');
export const aiPatchTextStyleOperationSchema = z.object({
    ...operationBase,
    type: z.literal('patch_text_style'),
    styleId: z.string().trim().min(1).max(120),
    breakpoint: aiBreakpointSchema,
    styles: aiTextStylePatchSchema,
}).strict();
export const aiSetVisibilityOperationSchema = z.object({
    ...operationBase,
    type: z.literal('set_visibility'),
    elementId: z.string().trim().min(1).max(160),
    breakpoint: aiBreakpointSchema,
    responsive: z.boolean().optional(),
    visible: z.boolean(),
}).strict();
export const aiMoveElementOperationSchema = z.object({
    ...operationBase,
    type: z.literal('move_element'),
    elementId: z.string().trim().min(1).max(160),
    targetId: z.string().trim().min(1).max(160),
    position: z.enum(['before', 'after', 'inside']),
}).strict();
export const aiDuplicateElementOperationSchema = z.object({
    ...operationBase,
    type: z.literal('duplicate_element'),
    elementId: z.string().trim().min(1).max(160),
}).strict();
export const aiDeleteElementOperationSchema = z.object({
    ...operationBase,
    type: z.literal('delete_element'),
    elementId: z.string().trim().min(1).max(160),
}).strict();
export const aiInsertComponentOperationSchema = z.object({
    ...operationBase,
    type: z.literal('insert_component'),
    componentId: z.string().trim().min(1).max(120).regex(/^[a-zA-Z0-9][a-zA-Z0-9_@.-]*$/),
    targetId: z.string().trim().min(1).max(160).nullable(),
    position: z.enum(['before', 'after', 'inside', 'page_end']),
    props: z.record(z.string().trim().min(1).max(80), z.string().max(5_000))
        .refine((props) => Object.keys(props).length <= 30, 'Too many component props'),
}).strict();
const aiLookIdSchema = z.string().trim().min(1).max(40).regex(/^[a-zA-Z0-9-]+$/);
/**
 * Un estilo a medida (ver `StyleRecipe` en @webbuilder/theme): uno de los
 * estilos de diseño del panel y, encima, el color de marca, la pareja de letras
 * o los looks que se pidan. «Brutalista pero verde» es el brutalista con el
 * verde. Sin estilo base, cada parte se aplica sobre el tema actual. La IA sólo
 * elige ids y un color; el editor lo aplica con las funciones del panel.
 */
export const aiApplyDesignStyleOperationSchema = z.object({
    ...operationBase,
    type: z.literal('apply_design_style'),
    designStyleId: aiLookIdSchema.optional(),
    paletteId: aiLookIdSchema.optional(),
    color: z.string().trim().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    fontPairId: z.string().trim().min(1).max(80).optional(),
    button: z.record(aiLookIdSchema, aiLookIdSchema).optional(),
    card: z.record(aiLookIdSchema, aiLookIdSchema).optional(),
    section: aiLookIdSchema.optional(),
}).strict();
export const aiEditorOperationSchema = z.discriminatedUnion('type', [
    aiSetContentOperationSchema,
    aiSetAttributeOperationSchema,
    aiPatchStylesOperationSchema,
    aiSetColorTokenOperationSchema,
    aiSetIconOperationSchema,
    aiPatchThemeColorsOperationSchema,
    aiPatchThemeFontsOperationSchema,
    aiPatchButtonStyleOperationSchema,
    aiPatchCardStyleOperationSchema,
    aiPatchIconStyleOperationSchema,
    aiPatchBrandLogoOperationSchema,
    aiPatchTextStyleOperationSchema,
    aiSetVisibilityOperationSchema,
    aiMoveElementOperationSchema,
    aiDuplicateElementOperationSchema,
    aiDeleteElementOperationSchema,
    aiInsertComponentOperationSchema,
    aiApplyDesignStyleOperationSchema,
]);
export const AI_EDITOR_ELEMENT_TYPES = [
    'section', 'div', 'text', 'richHeading', 'image', 'icon', 'badge', 'rating',
    'avatarGroup', 'stat', 'button', 'link', 'divider', 'embed',
];
export const aiEditorElementTypeSchema = z.enum(AI_EDITOR_ELEMENT_TYPES);
const aiContextStylesSchema = z.object({
    desktop: z.record(aiEditableStylePropertySchema, aiSafeCssValueSchema),
    tablet: z.record(aiEditableStylePropertySchema, aiSafeCssValueSchema).optional(),
    mobile: z.record(aiEditableStylePropertySchema, aiSafeCssValueSchema).optional(),
}).strict();
const aiContextStyleRefsSchema = z.object({
    typography: z.string().max(120).optional(),
    button: z.string().max(120).optional(),
    card: z.string().max(120).optional(),
    icon: z.string().max(120).optional(),
    colors: z.record(z.string().max(80), z.string().max(120)).optional(),
}).strict();
export const aiEditorElementContextSchema = z.object({
    id: z.string().trim().min(1).max(160),
    type: aiEditorElementTypeSchema,
    label: z.string().max(240).optional(),
    aiSlot: z.string().max(120).optional(),
    schemaId: z.string().max(160).optional(),
    sectionType: z.string().max(120).optional(),
    /** Whether AI may rewrite this node's copy without mutating navigation or CMS data. */
    contentSource: z.enum(['local', 'cms', 'navigation']).default('local'),
    parentId: z.string().max(160).nullable(),
    childIds: z.array(z.string().max(160)).max(AI_EDITOR_MAX_CONTEXT_ELEMENTS),
    content: z.string().max(5_000).optional(),
    accentContent: z.string().max(5_000).optional(),
    afterContent: z.string().max(5_000).optional(),
    alt: z.string().max(5_000).optional(),
    ariaLabel: z.string().max(5_000).optional(),
    href: z.string().max(2_000).optional(),
    src: z.string().max(2_000).optional(),
    iconName: z.string().max(120).optional(),
    iconSize: z.number().min(0).max(1_000).optional(),
    themeRole: z.string().max(120).optional(),
    styleRefs: aiContextStyleRefsSchema.optional(),
    /** Authored styles. Only sent for the selection roots, where local overrides matter. */
    styles: aiContextStylesSchema.optional(),
    /**
     * Effective styles after theme roles and color refs — a superset of `styles`,
     * so the detail tier sends this instead of both. Absent on outline elements.
     */
    resolvedStyles: aiContextStylesSchema.optional(),
}).strict();
export const aiChatMessageSchema = z.object({
    role: z.enum(['user', 'assistant']),
    text: z.string().min(1).max(5_000),
}).strict();
export const aiEditorRequestSchema = z.object({
    version: z.literal(AI_EDITOR_CONTRACT_VERSION),
    requestId: z.string().uuid(),
    projectId: z.string().trim().min(1).max(160),
    pageId: z.string().trim().min(1).max(160),
    conversationId: z.string().trim().min(1).max(160).optional(),
    message: z.string().trim().min(1).max(AI_EDITOR_MAX_MESSAGE_CHARS),
    /** Read-only copy to avoid repeating existing content; never an editable target. */
    referenceText: z.string().max(8_000).optional(),
    styleScope: z.enum(['brand', 'selection']).default('brand'),
    executionMode: z.enum(['standard', 'global_styles', 'content_batch', 'image_replace', 'image_batch', 'chrome_style', 'button_style', 'card_style', 'icon_style', 'brand_logo', 'theme_colors', 'element_style', 'icon_swap', 'theme_fonts', 'insert_block', 'text_style', 'section_background', 'design_style']).default('standard'),
    /**
     * What the previous turn actually did, in place of a transcript. Compact
     * routes carry no history, so "un poco más" or "ahora en azul" had nothing to
     * resolve against; the applied state answers those better than four
     * paragraphs of prose and costs about a tenth as much.
     */
    sessionState: z.object({
        route: z.string().trim().min(1).max(40),
        values: z.record(z.string().max(40), z.string().max(120)).optional(),
        targets: z.number().int().min(0).max(999).optional(),
    }).strict().optional(),
    batchSession: z.object({
        id: z.string().uuid(),
        index: z.number().int().min(0).max(29),
        total: z.number().int().min(1).max(30),
    }).strict().refine((batch) => batch.index < batch.total, 'Batch index must be inside total').optional(),
    breakpoint: aiBreakpointSchema,
    interactionState: aiInteractionStateSchema,
    selectedElementIds: z.array(z.string().max(160)).max(50),
    selectionScope: z.object({
        rootElementIds: z.array(z.string().max(160)).max(50),
        /**
         * Redundant with the parent/child links in `elements`, so the editor no longer
         * sends it. Kept optional for older clients still on the previous payload.
         */
        descendantIds: z.array(z.string().max(160)).max(AI_EDITOR_MAX_CONTEXT_ELEMENTS).optional(),
        includesDescendants: z.boolean(),
        /** True when some descendant of the selection only made it into the outline tier. */
        truncated: z.boolean(),
    }).strict().optional(),
    elements: z.array(aiEditorElementContextSchema).max(AI_EDITOR_MAX_CONTEXT_ELEMENTS),
    themeColors: z.record(aiThemeColorTokenSchema, aiSafeCssValueSchema),
    themeFonts: z.object({
        headingFont: z.string().trim().min(1).max(240),
        bodyFont: z.string().trim().min(1).max(240),
    }).strict(),
    cardStyles: z.array(z.object({
        id: z.enum(['card-default', 'card-soft', 'card-elevated']),
        styles: aiCardStyleValuesSchema,
    }).strict()).max(3).default([]),
    buttonStyles: z.array(z.object({
        id: z.enum(['button-primary', 'button-secondary']),
        styles: aiButtonStyleValuesSchema,
    }).strict()).max(2).default([]),
    iconStyles: z.array(z.object({
        id: z.enum(['icon-primary', 'icon-secondary', 'icon-accent']),
        styles: aiIconStyleValuesSchema,
        iconSize: z.number().int().min(1).max(256),
    }).strict()).max(3).default([]),
    brandLogo: z.object({
        name: z.string().max(60),
        nameFont: z.string().max(240),
        nameColor: z.string().max(240),
        nameColorDark: z.string().max(240),
        nameSize: z.string().max(40),
        nameWeight: z.string().max(40),
        mark: z.enum(['none', 'icon', 'image']),
        iconName: z.string().max(120),
        iconColor: z.string().max(240),
        iconColorDark: z.string().max(240),
        iconSize: z.string().max(40),
        iconBackground: z.string().max(240),
        iconBackgroundDark: z.string().max(240),
        iconBackgroundOpacity: z.number().min(0).max(100),
        iconRadius: z.string().max(40),
        imageLight: z.string().max(2_000),
        imageDark: z.string().max(2_000),
        imageSize: z.string().max(40),
        showName: z.boolean(),
    }).strict().optional(),
    textStyles: z.array(z.object({
        id: z.enum(AI_GLOBAL_TEXT_STYLE_IDS),
        styles: z.record(aiTextStylePropertySchema, aiSafeCssValueSchema),
    }).strict()).max(AI_GLOBAL_TEXT_STYLE_IDS.length).default([]),
    capabilities: z.object({
        operations: z.array(aiEditorOperationTypeSchema).max(AI_EDITOR_OPERATION_TYPES.length),
        componentIds: z.array(z.string().trim().min(1).max(120).regex(/^[a-zA-Z0-9][a-zA-Z0-9_@.-]*$/)).max(100),
        iconNames: z.array(z.string().max(120)).max(200).default([]),
    }).strict(),
    history: z.array(aiChatMessageSchema).max(20).default([]),
    /**
     * El idioma del sitio, para que el texto nuevo salga en él y no en el del
     * prompt. Opcional: sin él, el modelo sigue el idioma de la página.
     */
    siteLanguage: z.enum(['es', 'en']).optional(),
    /** El estilo de diseño puesto, si es uno de los del panel: el bloque elegido casa con él. */
    designStyleId: z.string().trim().min(1).max(40).regex(/^[a-z0-9-]+$/).optional(),
    /** «Otra variante»: bloques ya probados en esta petición, que no deben volver a salir. */
    excludeBlockIds: z.array(z.string().trim().min(1).max(120)).max(20).optional(),
    /**
     * Lo que debe mostrar la foto, en palabras clave en inglés, tal como lo dio
     * el planificador (`AiPlanStep.query`). Con él, la galería se busca por eso y
     * no por las palabras del mensaje: «taza» no casaba con «mug».
     */
    imageQuery: z.string().trim().min(2).max(60).optional(),
}).strict();
export const aiEditorResponseSchema = z.object({
    version: z.literal(AI_EDITOR_CONTRACT_VERSION),
    message: z.string().trim().min(1).max(5_000),
    summary: z.string().trim().min(1).max(500),
    operations: z.array(aiEditorOperationSchema).max(AI_EDITOR_MAX_OPERATIONS),
    requiresConfirmation: z.boolean(),
    warnings: z.array(z.string().trim().min(1).max(500)).max(10).default([]),
}).strict().superRefine((response, context) => {
    const hasDestructiveOperation = response.operations.some((operation) => operation.type === 'delete_element');
    if (hasDestructiveOperation && !response.requiresConfirmation) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['requiresConfirmation'],
            message: 'Deleting elements requires confirmation',
        });
    }
});
function providerRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
        ? value
        : null;
}
/** Accepts a CSS stack only when its first family is in the editor catalog. */
function normalizeProviderFontFamily(value) {
    if (typeof value !== 'string')
        return value;
    const firstFamily = value
        .split(',')[0]
        ?.trim()
        .replace(/^(['"])(.*)\1$/, '$2');
    if (!firstFamily)
        return value;
    const catalogFamily = AI_FONT_FAMILIES.find((family) => family.toLowerCase() === firstFamily.toLowerCase());
    if (catalogFamily)
        return catalogFamily;
    // Models regularly express a visual request as a generic CSS family.  Those
    // are valid intent, but not values the editor can load, so resolve them to a
    // close catalog family instead of rejecting the whole change.
    const genericFamily = firstFamily.toLowerCase().replace(/["']/g, '');
    if (['sans-serif', 'sans serif', 'system-ui', 'ui-sans-serif'].includes(genericFamily))
        return 'Inter';
    if (['serif', 'ui-serif'].includes(genericFamily))
        return 'Playfair Display';
    return value;
}
/** Normalizes harmless provider formatting without weakening the operation schema. */
export function normalizeAiEditorProviderPayload(value) {
    const response = providerRecord(value);
    if (!response || !Array.isArray(response.operations))
        return value;
    const providerOperations = response.operations;
    return {
        ...response,
        operations: providerOperations.map((operation) => {
            const record = providerRecord(operation);
            if (record?.type === 'patch_styles') {
                const styles = providerRecord(record.styles);
                if (!styles?.fontFamily)
                    return operation;
                return { ...record, styles: { ...styles, fontFamily: normalizeProviderFontFamily(styles.fontFamily) } };
            }
            if (record?.type !== 'patch_theme_fonts')
                return operation;
            return {
                ...record,
                headingFont: normalizeProviderFontFamily(record.headingFont),
                bodyFont: normalizeProviderFontFamily(record.bodyFont),
            };
        }),
    };
}
/**
 * Parses a model response and, when only individual operations are malformed,
 * keeps the valid operations instead of rejecting the complete proposal.
 */
export function parseAiEditorProviderResponse(value) {
    const normalized = normalizeAiEditorProviderPayload(value);
    const direct = aiEditorResponseSchema.safeParse(normalized);
    if (direct.success) {
        return { success: true, data: direct.data, rejectedOperations: 0, issues: [] };
    }
    const response = providerRecord(normalized);
    if (!response || !Array.isArray(response.operations))
        return direct;
    const operations = [];
    const issues = [];
    let rejectedOperations = Math.max(0, response.operations.length - AI_EDITOR_MAX_OPERATIONS);
    for (const [index, operation] of response.operations.slice(0, AI_EDITOR_MAX_OPERATIONS).entries()) {
        const parsed = aiEditorOperationSchema.safeParse(operation);
        if (parsed.success) {
            operations.push(parsed.data);
            continue;
        }
        rejectedOperations += 1;
        issues.push(...parsed.error.issues.map((issue) => ({
            ...issue,
            path: ['operations', index, ...issue.path],
        })));
    }
    if (rejectedOperations === 0 || operations.length === 0)
        return direct;
    const existingWarnings = Array.isArray(response.warnings)
        ? response.warnings
            .filter((warning) => typeof warning === 'string' && warning.trim().length > 0)
            .map((warning) => warning.trim().slice(0, 500))
            .slice(0, 9)
        : [];
    const rejectedWarning = rejectedOperations === 1
        ? 'Se descartó 1 cambio que el editor no admite.'
        : 'Se descartaron ' + String(rejectedOperations) + ' cambios que el editor no admite.';
    const recovered = aiEditorResponseSchema.safeParse({
        version: response.version,
        message: response.message,
        summary: response.summary,
        operations,
        requiresConfirmation: response.requiresConfirmation === true
            || operations.some((operation) => operation.type === 'delete_element'),
        warnings: [
            ...existingWarnings,
            rejectedWarning,
        ],
    });
    return recovered.success
        ? { success: true, data: recovered.data, rejectedOperations, issues }
        : direct;
}
export const AI_GLOBAL_CARD_STYLE_IDS = ['card-default', 'card-soft', 'card-elevated'];
export const AI_GLOBAL_BUTTON_STYLE_IDS = ['button-primary', 'button-secondary'];
export const AI_GLOBAL_ICON_STYLE_IDS = ['icon-primary', 'icon-secondary', 'icon-accent'];
/** Apply a brand button request to both actual button tokens, never button-link. */
export function expandGlobalButtonStyleOperations(response) {
    const buttonOperations = response.operations.filter((operation) => operation.type === 'patch_button_style');
    if (buttonOperations.length === 0)
        return response;
    const byStyleId = new Map(buttonOperations.map((operation) => [operation.styleId, operation]));
    const fallback = buttonOperations[0];
    const usedIds = new Set(response.operations.map((operation) => operation.id));
    const complete = AI_GLOBAL_BUTTON_STYLE_IDS.map((styleId) => {
        const existing = byStyleId.get(styleId);
        if (existing)
            return existing;
        let id = 'global-' + styleId;
        let suffix = 2;
        while (usedIds.has(id))
            id = 'global-' + styleId + '-' + String(suffix++);
        usedIds.add(id);
        return {
            ...fallback,
            id,
            styleId,
            reason: 'Aplicar el cambio a los estilos globales de botón sin modificar enlaces.',
        };
    });
    const nonButtonOperations = response.operations
        .filter((operation) => operation.type !== 'patch_button_style')
        .slice(0, AI_EDITOR_MAX_OPERATIONS - complete.length);
    return { ...response, operations: [...nonButtonOperations, ...complete] };
}
/**
 * A brand-level card change is one logical operation applied to every built-in
 * card token. Preserve token-specific patches when the provider supplied them;
 * otherwise copy its first valid card patch to each missing token.
 */
export function expandGlobalCardStyleOperations(response) {
    const cardOperations = response.operations.filter((operation) => operation.type === 'patch_card_style');
    if (cardOperations.length === 0)
        return response;
    const byStyleId = new Map(cardOperations.map((operation) => [operation.styleId, operation]));
    const fallback = cardOperations[0];
    const usedIds = new Set(response.operations.map((operation) => operation.id));
    const uniqueId = (styleId) => {
        const base = 'global-' + styleId;
        let candidate = base;
        let suffix = 2;
        while (usedIds.has(candidate))
            candidate = base + '-' + String(suffix++);
        usedIds.add(candidate);
        return candidate;
    };
    const completeCardOperations = AI_GLOBAL_CARD_STYLE_IDS.map((styleId) => {
        const existing = byStyleId.get(styleId);
        return existing ?? {
            ...fallback,
            id: uniqueId(styleId),
            styleId,
            reason: 'Aplicar el cambio solicitado a todos los estilos globales de tarjeta.',
        };
    });
    const nonCardOperations = response.operations
        .filter((operation) => operation.type !== 'patch_card_style')
        .slice(0, AI_EDITOR_MAX_OPERATIONS - completeCardOperations.length);
    const operations = [...nonCardOperations, ...completeCardOperations];
    return { ...response, operations };
}
export function expandGlobalIconStyleOperations(response) {
    const iconOperations = response.operations.filter((operation) => operation.type === 'patch_icon_style');
    if (iconOperations.length === 0)
        return response;
    const byStyleId = new Map(iconOperations.map((operation) => [operation.styleId, operation]));
    const fallback = iconOperations[0];
    const usedIds = new Set(response.operations.map((operation) => operation.id));
    const complete = AI_GLOBAL_ICON_STYLE_IDS.map((styleId) => {
        const existing = byStyleId.get(styleId);
        if (existing)
            return existing;
        let id = 'global-' + styleId;
        let suffix = 2;
        while (usedIds.has(id))
            id = 'global-' + styleId + '-' + String(suffix++);
        usedIds.add(id);
        return {
            ...fallback,
            id,
            styleId,
            reason: 'Aplicar el cambio a todos los tokens globales de icono.',
        };
    });
    const nonIconOperations = response.operations
        .filter((operation) => operation.type !== 'patch_icon_style')
        .slice(0, AI_EDITOR_MAX_OPERATIONS - complete.length);
    return { ...response, operations: [...nonIconOperations, ...complete] };
}
//# sourceMappingURL=aiEditor.js.map